import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { toast } from "sonner";
import * as LucideIcons from "lucide-react";
import { Trophy, Flame, Star, Award, Crown, Medal, Loader2, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DailyChallenge = () => {
  const [challenge, setChallenge] = useState(null);
  const [badges, setBadges] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchChallenge();
    fetchBadges();
  }, []);

  const fetchChallenge = async () => {
    try {
      const response = await axios.get(`${API}/challenges/today`);
      setChallenge(response.data);
    } catch (error) {
      console.error("Error fetching challenge:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API}/challenges/badges`);
      setBadges(response.data);
    } catch (error) {
      console.error("Error fetching badges:", error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/challenges/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : <Star className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-700/50">
        <CardContent className="p-5 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
        </CardContent>
      </Card>
    );
  }

  if (!challenge || !challenge.challenge) {
    return null;
  }

  const progressPercent = (challenge.progress / challenge.challenge.target) * 100;

  return (
    <>
      <Card className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-700/50 overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                challenge.completed 
                  ? "bg-gba-green text-white" 
                  : "bg-amber-600/30 text-amber-400"
              }`}>
                {challenge.completed ? (
                  <Trophy className="w-7 h-7" />
                ) : (
                  getIcon(challenge.challenge.icon)
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-amber-400 uppercase tracking-wider">
                    Daily Challenge
                  </span>
                  <span className="px-2 py-0.5 bg-amber-600/30 rounded-full text-xs text-amber-300">
                    +{challenge.challenge.points} pts
                  </span>
                </div>
                <h3 className="font-semibold text-white text-lg">
                  {challenge.challenge.title}
                </h3>
                <p className="text-slate-400 text-sm mt-0.5">
                  {challenge.challenge.description}
                </p>
              </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    fetchBadges();
                    fetchLeaderboard();
                  }}
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                  data-testid="view-badges-button"
                >
                  <Award className="w-4 h-4 mr-1" />
                  Badges
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1E293B] border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-white flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-amber-400" />
                    Achievements & Leaderboard
                  </DialogTitle>
                </DialogHeader>

                {badges && (
                  <div className="mt-4 space-y-6">
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-400">{badges.points}</p>
                        <p className="text-xs text-slate-400">Total Points</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-orange-400">{badges.stats.streak}</p>
                        <p className="text-xs text-slate-400">Day Streak</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-gba-green">
                          {badges.badges.filter(b => b.earned).length}/{badges.badges.length}
                        </p>
                        <p className="text-xs text-slate-400">Badges</p>
                      </div>
                    </div>

                    {/* Badges */}
                    <div>
                      <h4 className="font-medium text-white mb-3">Badges</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {badges.badges.map((badge) => (
                          <div
                            key={badge.id}
                            className={`p-3 rounded-lg border ${
                              badge.earned
                                ? "bg-amber-900/30 border-amber-700"
                                : "bg-slate-800/50 border-slate-700 opacity-60"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                badge.earned ? "bg-amber-600 text-white" : "bg-slate-700 text-slate-400"
                              }`}>
                                {getIcon(badge.icon)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">{badge.name}</p>
                              </div>
                            </div>
                            <p className="text-xs text-slate-400 mb-2">{badge.description}</p>
                            <Progress 
                              value={(badge.progress / badge.target) * 100} 
                              className="h-1.5 bg-slate-700"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              {badge.progress}/{badge.target}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Leaderboard */}
                    <div>
                      <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-400" />
                        Leaderboard
                      </h4>
                      <div className="space-y-2">
                        {leaderboard.slice(0, 10).map((user, index) => (
                          <div
                            key={index}
                            className={`flex items-center gap-3 p-3 rounded-lg ${
                              index < 3 ? "bg-amber-900/20 border border-amber-800/50" : "bg-slate-800/50"
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              index === 0 ? "bg-amber-500 text-white" :
                              index === 1 ? "bg-slate-400 text-slate-900" :
                              index === 2 ? "bg-amber-700 text-white" :
                              "bg-slate-700 text-slate-300"
                            }`}>
                              {user.rank}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-white">{user.name}</p>
                              <div className="flex items-center gap-3 text-xs text-slate-400">
                                <span className="flex items-center gap-1">
                                  <Flame className="w-3 h-3 text-orange-400" />
                                  {user.streak} days
                                </span>
                                <span className="flex items-center gap-1">
                                  <Medal className="w-3 h-3 text-amber-400" />
                                  {user.badges_count} badges
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-amber-400">{user.points}</p>
                              <p className="text-xs text-slate-500">points</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Progress</span>
              <span className={`text-sm font-medium ${challenge.completed ? "text-gba-green" : "text-white"}`}>
                {challenge.progress}/{challenge.challenge.target}
                {challenge.completed && " ✓ Complete!"}
              </span>
            </div>
            <Progress 
              value={progressPercent} 
              className={`h-3 ${challenge.completed ? "bg-gba-green/20" : "bg-slate-700"}`}
            />
          </div>

          {challenge.completed && (
            <div className="mt-3 p-3 bg-gba-green/20 rounded-lg border border-gba-green/30">
              <p className="text-sm text-gba-green font-medium text-center">
                🎉 Challenge Complete! +{challenge.challenge.points} points earned!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default DailyChallenge;
